import React, { useState } from 'react';
import logo from '../../assets/logo-lockup.png';
import {
  LISTING_FEE_CENTS,
  LISTING_FEE_PREMIUM_CENTS,
  LISTING_FEE_PREMIUM_MIN_PRICE,
  LISTING_SIDE_COMMISSION_LABEL,
  formatDollars,
  listingFeeCents,
  listingSideCommission,
} from '../../utils/pricing';
import { PRICING_DISCLAIMER } from '../../utils/disclaimers';
import { savePendingListing } from '../../utils/pendingListing';
import { SERVICE_AREA_LABEL, SERVICE_STATES } from '../../utils/publicSite';
import AuthHomeLink from '../Auth/AuthHomeLink';
import '../Auth/AuthPage.css';
import styles from './ListYourHomeStart.module.css';

const EMPTY_FORM = { address: '', city: '', state: '', zip_code: '', home_value: '' };

/**
 * Parse a loosely typed dollar amount ("450,000", "$450k" is not supported).
 *
 * @param {string} value
 * @returns {number|null} Whole dollars, or null when blank/invalid.
 */
function parseDollars(value) {
  const digits = String(value || '').replace(/[^0-9.]/g, '');
  if (!digits) return null;
  const amount = Math.round(Number(digits));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/**
 * Public "start your listing" page, shown to signed-out visitors.
 *
 * The visitor enters the property address (and, optionally, a rough value) and
 * sees what listing on cutthe6.com costs next to a typical commission. They
 * then create an account or sign in; the address is saved so the Post Listing
 * wizard opens with it filled in.
 *
 * @param {object} props
 * @param {() => void} props.onCreateAccount - Go to signup (listing intent).
 * @param {() => void} props.onSignIn - Go to the login page.
 * @param {() => void} [props.onHome] - Back to the public home page.
 * @param {() => void} [props.onJoinWaitlist] - Open the other-states waitlist.
 */
export default function ListYourHomeStart({ onCreateAccount, onSignIn, onHome, onJoinWaitlist }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [showQuote, setShowQuote] = useState(false);
  // A home outside the service area gets the waitlist, not a quote and signup.
  const [outOfArea, setOutOfArea] = useState(false);

  const set = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: field === 'state' ? value.toUpperCase() : value }));
  };

  const homeValue = parseDollars(form.home_value);
  const feeCents = listingFeeCents(homeValue);
  // Listing side only, same estimate as the pricing page (utils/pricing.js).
  const commission = homeValue ? listingSideCommission(homeValue) : null;
  const kept = commission != null ? commission - feeCents / 100 : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.address.trim() || !form.city.trim() || !form.state.trim() || !form.zip_code.trim()) {
      setError('Enter the full property address to continue.');
      return;
    }
    if (!/^[A-Za-z]{2}$/.test(form.state.trim())) {
      setError('Use the two-letter state code, like WI.');
      return;
    }
    setError('');
    const served = SERVICE_STATES.some((s) => s.code === form.state.trim().toUpperCase());
    setOutOfArea(!served);
    setShowQuote(served);
  };

  const continueWith = (next) => {
    savePendingListing(form);
    next();
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <AuthHomeLink onHome={onHome} />
        <div className="auth-brand auth-brand--logo">
          <img src={logo} alt="cutthe6.com" className="auth-brand-logo" />
          <p className="auth-brand-tagline">Sell your home. Keep more of your money.</p>
        </div>
        <h1 className="auth-title">List your home</h1>
        <p className="auth-subtitle">
          Start with the address. You&apos;ll create your account next, then finish the listing.
        </p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate data-testid="list-home-form">
          <div className="auth-field">
            <label htmlFor="lyh-address" className="auth-label">Street address</label>
            <input
              id="lyh-address"
              className="auth-input"
              value={form.address}
              onChange={set('address')}
              autoComplete="street-address"
              placeholder="123 Main St"
              required
            />
          </div>
          <div className="auth-row">
            <div className="auth-field">
              <label htmlFor="lyh-city" className="auth-label">City</label>
              <input
                id="lyh-city"
                className="auth-input"
                value={form.city}
                onChange={set('city')}
                autoComplete="address-level2"
                required
              />
            </div>
            <div className={`auth-field ${styles.stateField}`}>
              <label htmlFor="lyh-state" className="auth-label">State</label>
              <input
                id="lyh-state"
                className="auth-input"
                value={form.state}
                onChange={set('state')}
                autoComplete="address-level1"
                maxLength={2}
                placeholder="WI"
                required
              />
            </div>
            <div className="auth-field">
              <label htmlFor="lyh-zip" className="auth-label">ZIP</label>
              <input
                id="lyh-zip"
                className="auth-input"
                value={form.zip_code}
                onChange={set('zip_code')}
                autoComplete="postal-code"
                inputMode="numeric"
                required
              />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="lyh-value" className="auth-label">
              Rough home value <span className="auth-optional">(optional)</span>
            </label>
            <input
              id="lyh-value"
              className="auth-input"
              value={form.home_value}
              onChange={set('home_value')}
              inputMode="numeric"
              placeholder="450,000"
            />
          </div>

          {!showQuote && (
            <button type="submit" className="auth-btn-primary">
              See what you&apos;d pay
            </button>
          )}
        </form>

        {outOfArea && (
          <section className={styles.quote} role="status" data-testid="list-home-out-of-area">
            <p className={styles.outOfAreaTitle}>
              cutthe6.com serves {SERVICE_AREA_LABEL} homes only for now.
            </p>
            {/* The waitlist lives on the public site; no handler, no pitch. */}
            {onJoinWaitlist && (
              <>
                <p className={styles.feeNote}>
                  Join the waitlist and we&apos;ll tell you when we open in your state.
                </p>
                <button type="button" className="auth-btn-primary" onClick={onJoinWaitlist}>
                  Join the waitlist
                </button>
              </>
            )}
          </section>
        )}

        {showQuote && (
          <section className={styles.quote} aria-live="polite" data-testid="list-home-quote">
            <div className={styles.feeRow}>
              <span className={styles.feeLabel}>Your cutthe6.com listing fee</span>
              <span className={styles.feeAmount} data-testid="list-home-fee">
                {formatDollars(feeCents)}
              </span>
            </div>
            <p className={styles.feeNote}>
              One flat software fee, paid when you publish: {formatDollars(LISTING_FEE_CENTS)},
              or {formatDollars(LISTING_FEE_PREMIUM_CENTS)} for homes listed at
              {' '}${LISTING_FEE_PREMIUM_MIN_PRICE.toLocaleString('en-US')} or more.
            </p>
            {commission != null && (
              <>
                <div className={styles.compareRow}>
                  <span>Typical listing-agent commission (est. {LISTING_SIDE_COMMISSION_LABEL})</span>
                  <span data-testid="list-home-commission">{formatDollars(commission * 100)}</span>
                </div>
                {kept > 0 && (
                  <p className={styles.kept} data-testid="list-home-kept">
                    You could keep about {formatDollars(kept * 100)}.
                  </p>
                )}
                <p className={styles.estimateNote}>
                  Illustrative estimate. Commissions vary and are negotiable, and a
                  listing agent provides services we don&apos;t. Any buyer&apos;s-agent
                  pay is separate and not included.
                </p>
              </>
            )}

            <button
              type="button"
              className="auth-btn-primary"
              onClick={() => continueWith(onCreateAccount)}
              data-testid="list-home-create-account"
            >
              Create your account to continue
            </button>
            <p className="auth-switch">
              Already have an account?{' '}
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => continueWith(onSignIn)}
              >
                Sign in
              </button>
            </p>
          </section>
        )}

        {!showQuote && (
          <p className="auth-switch">
            Already have an account?{' '}
            <button type="button" className="auth-link-btn" onClick={onSignIn}>
              Sign in
            </button>
          </p>
        )}

        <p className={styles.disclaimer}>{PRICING_DISCLAIMER}</p>
      </div>
    </div>
  );
}
